import { createContext, useContext, type PropsWithChildren } from 'react';
import { Outlet } from 'react-router-dom';
import { subscribeMyBusinesses } from '../shared/api/businesses.js';
import type { BusinessDoc } from '../shared/api/converters.js';
import { keys } from '../shared/api/keys.js';
import { useLiveQuery } from '../shared/api/useLiveQuery.js';
import { useAuth } from '../features/auth/index.js';

interface BusinessesState {
  /** All businesses the account owns, oldest first (includes archived). */
  businesses: BusinessDoc[];
  /** Non-archived businesses — what the switcher lists. */
  active: BusinessDoc[];
  loading: boolean;
}

const BusinessesContext = createContext<BusinessesState>({
  businesses: [],
  active: [],
  loading: true,
});

/**
 * Loads every business the signed-in owner has as a live query (doc 07 §8.2).
 * Usable both as a wrapper (`children`) and as a react-router layout route
 * (renders `<Outlet/>` when no children are passed).
 */
export function BusinessesProvider({ children }: PropsWithChildren) {
  const { user } = useAuth();
  const uid = user?.uid ?? '';

  const list = useLiveQuery<BusinessDoc[]>(keys.myBusinesses(uid), (cb) =>
    subscribeMyBusinesses(uid, cb),
  );

  const businesses = list.data ?? [];
  return (
    <BusinessesContext.Provider
      value={{
        businesses,
        active: businesses.filter((b) => b.status !== 'archived'),
        loading: list.isLoading,
      }}
    >
      {children ?? <Outlet />}
    </BusinessesContext.Provider>
  );
}

export function useMyBusinesses(): BusinessesState {
  return useContext(BusinessesContext);
}
